import { CTA, Layout, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { PriceEstimateWithoutServicePlanPageViewProps } from './PriceEstimateWithoutServicePlanPageViewProps';
import { PartEstimation, RepairOperationEstimation } from '../../modules/entities/Entities';
import { Form } from '../../modules/forms/Form';
import { priceEstimateWithoutServicePlanPageFormSchema } from './PriceEstimateWithoutServicePlanPageFormSchema';
import { CheckboxField } from '../../modules/forms/fields/CheckboxField';

const StyledServiceDescription = styled(Text)`
  margin-top: ${(props) => props.theme.size.dynamic0100};
`;
const StyledValueWithUnit = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.static200};
  margin: ${(props) => props.theme.size.static400} 0;

  & div {
    display: flex;
    align-items: flex-end;
    gap: ${(props) => props.theme.size.static150};
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-top: 1px solid;
  border-bottom: 1px solid;
  border-collapse: collapse;
  margin-bottom: ${(props) => props.theme.size.static400};
`;
const StyledTd = styled.td`
  width: 50%;
  border-left: 1px solid;
  border-right: 1px solid;

  &:first-of-type {
    border-left: none;
  }

  &:last-of-type {
    border-right: none;
  }
`;
const StyledTr = styled.tr`
  border-top: 1px solid;
  border-bottom: 1px solid;
`;
const StyledTableHeader = styled.thead`
  border-top: 2px solid;
`;
const StyledTableFooter = styled.tfoot`
  border-top: 2px solid;
`;

const StyledTermsAndConditionsContainer = styled.div`
  display: flex;
  align-items: center;
`;

function zipArrays(
  a: PartEstimation[],
  b: RepairOperationEstimation[],
): [PartEstimation | undefined, RepairOperationEstimation | undefined][] {
  return Array.from(Array(Math.max(b.length, a.length)), (_, i) => [a[i], b[i]]);
}

export function PriceEstimateWithoutServicePlanPageVwView({
  serviceRequest,
  formRef,
  initialFormData,
  tcosEstimation,
  vehicleServiceHistory,
  onTermsAndConditionsClick,
}: PriceEstimateWithoutServicePlanPageViewProps): ReactElement {
  return (
    <Layout allowOverflowingContent>
      <Text appearance={TokenTextAppearance.headline300} bold>
        {serviceRequest.serviceType.name}
      </Text>
      <StyledServiceDescription appearance={TokenTextAppearance.copy200}>
        {vehicleServiceHistory.serviceDescription}
      </StyledServiceDescription>
      <Form defaultValues={initialFormData} ref={formRef} schema={priceEstimateWithoutServicePlanPageFormSchema}>
        <StyledValueWithUnit>
          <Text appearance={TokenTextAppearance.copy200}>Total cost of the service</Text>
          <Text appearance={TokenTextAppearance.headline400} bold>
            40
            <Text appearance={TokenTextAppearance.headline200} bold={false}>
              $
            </Text>
          </Text>
        </StyledValueWithUnit>
        <StyledTable>
          <StyledTableHeader>
            <StyledTr>
              <StyledTd>
                <Text appearance={TokenTextAppearance.bigcopy100} bold>
                  Parts
                </Text>
              </StyledTd>
              <StyledTd>
                <Text appearance={TokenTextAppearance.bigcopy100} bold>
                  Labours
                </Text>
              </StyledTd>
            </StyledTr>
          </StyledTableHeader>
          <tbody>
            {zipArrays(tcosEstimation.partsEstimations, tcosEstimation.repairOperationsEstimations).map((pair) => (
              <StyledTr key={`${pair[0]?.partNumber} ${pair[1]?.operationCode}`}>
                <StyledTd>
                  {pair[0] && <Text appearance={TokenTextAppearance.bigcopy100}>{`${pair[0].partNumber} ${pair[0].price}$`}</Text>}
                </StyledTd>
                <StyledTd>
                  {pair[1] && <Text appearance={TokenTextAppearance.bigcopy100}>{`${pair[1].operationCode} ${pair[1].price}$`}</Text>}
                </StyledTd>
              </StyledTr>
            ))}
          </tbody>
          <StyledTableFooter>
            <StyledTr>
              <StyledTd>
                <Text appearance={TokenTextAppearance.bigcopy100} bold>
                  Parts
                </Text>
              </StyledTd>
              <StyledTd>
                <Text appearance={TokenTextAppearance.bigcopy100} bold>
                  Labours
                </Text>
              </StyledTd>
            </StyledTr>
          </StyledTableFooter>
        </StyledTable>
        <StyledTermsAndConditionsContainer>
          <CheckboxField name="termsAndConditionsAccepted" label={<Text>I accept the&nbsp;</Text>} />
          <CTA tag="a" emphasis="tertiary" onClick={onTermsAndConditionsClick}>
            <Text>terms and conditions</Text>
          </CTA>
        </StyledTermsAndConditionsContainer>
      </Form>
    </Layout>
  );
}
